import type { RouteLocationRaw, RouteMeta, RouteRecordRaw } from 'vue-router'
import type { ComposeMessageRouteQueryParams } from '@/components/views/compose/types'
import { scheduleRouteHash } from '@/components/views/compose/types'
import { AppModule } from '@/config/types'
import type { RedirectByPrevRouteResolverParams } from '@/services/resolvers/types'
import type { TmNamedRouteRecord } from '@/services/route/types'
import EmailCampaignSettings from '@/data/models/domain/campaign/EmailCampaignSettings'
import AsyncResolver from '@/components/resolvers/AsyncResolver.vue'
import ComposeMessageResolver from '@/components/resolvers/ComposeMessageResolver.vue'

export const composeMessageRouteName = 'user.compose.message'
export const composeSuccessMessageRouteName = 'user.compose.success-message'
export const composeSuccessMessagesRouteName = 'user.compose.success-messages'
export const composeSuccessScheduledRouteName = 'user.compose.success-scheduled'
export const composeEmailRouteName = 'user.compose.email'

export const composeMessageRoute: RouteLocationRaw = {
  name: composeMessageRouteName,
}

export type ComposeMessageRouteRaw = {
  openSchedule?: boolean
}

export const getComposeMessageRoute = (routeRaw?: ComposeMessageRouteRaw): RouteLocationRaw => ({
  name: composeMessageRouteName,
  hash: routeRaw?.openSchedule ? scheduleRouteHash : '',
})

export const composeTextMessageRoute: TmNamedRouteRecord = {
  name: composeMessageRouteName,
  path: 'text-message',
  meta: {
    tabTitle: 'pageTabTitle.user.compose.message',
    resolvers: [
      [
        {
          service: 'UserNumbersResolverService',
        },
        {
          service: 'SenderSettingsCountriesResolverService',
        },
        {
          service: 'ComposeSmsResolverService',
        },
      ],
    ],
    resolverComponent: ComposeMessageResolver,
  },
  props: (route) => ({
    openSchedule: route.hash === scheduleRouteHash,
    openCreditModal: (route.query as ComposeMessageRouteQueryParams).openCreditModal === 'true',
  }),
  component: () => import('@/components/views/compose/ComposeMessageWrapper.vue'),
}

export const composeEmailRoute: TmNamedRouteRecord = {
  name: composeEmailRouteName,
  path: 'email',
  meta: {
    tabTitle: 'pageTabTitle.user.compose.email',
    resolvers: [
      {
        service: 'ModelSettingsResolverService',
        params: {
          model: EmailCampaignSettings,
        },
      },
      {
        service: 'EmailCampaignDomainWebSocketResolver',
      },
      {
        service: 'EmailCampaignDomainsResolverService',
      },
    ],
    resolverComponent: AsyncResolver,
  },
  component: () => import('@/components/views/compose/ComposeEmail.vue'),
}

const successRoutesMeta: RouteMeta = {
  resolvers: [
    {
      service: 'RedirectByPrevRouteResolverService',
      params: {
        grantAccessFrom: [composeMessageRouteName],
        redirectTo: composeMessageRouteName,
      } as RedirectByPrevRouteResolverParams,
    },
  ],
}

export const getComposeRoutes = (): RouteRecordRaw[] => [
  {
    name: 'user.compose',
    path: 'compose',
    meta: {
      tags: ['USER_TOP_MENU'],
      tabTitle: 'pageTabTitle.user.compose.compose',
      resolvers: [
        {
          service: 'ComposeRouteResolver',
        },
        {
          service: 'ComposeEmailResolver',
        },
      ],
      resolverComponent: AsyncResolver,
      modules: [
        AppModule.SenderSettingsCountries,
        AppModule.Contacts,
        AppModule.ContactList,
        AppModule.ScheduledCalendar,
        AppModule.ScheduledNotSent,
        AppModule.RRule,
        AppModule.MessageSend,
        AppModule.Compose,
        AppModule.ComposeEmail,
        AppModule.RecentRecipient,
        AppModule.ContactCounter,
        AppModule.SentSmsBase,
        AppModule.Templates,
        AppModule.FilteredViews,
        AppModule.Segments,
        AppModule.Recipient,
        AppModule.CampaignsSenders,
        AppModule.TenDlcTollFree, // TollFreePhoneNumbersService via ComposeSenderAlerts → ComposeSenderFormLine → ComposeMessage
        AppModule.Labels,
        AppModule.TmCodemirrorEditor,
      ],
    },
    component: () => import('@/components/views/compose/Compose.vue'),
    children: [
      composeTextMessageRoute as RouteRecordRaw,
      {
        name: composeSuccessMessageRouteName,
        path: 'success-message/:id',
        props: true,
        component: () => import('@/components/views/compose/ComposeSuccessMessage.vue'),
        meta: {
          ...successRoutesMeta,
          tabTitle: 'pageTabTitle.user.compose.sentSmsDetails',
          titler: {
            service: 'RouteTitlerService',
          },
        },
      },
      {
        name: composeSuccessMessagesRouteName,
        path: 'success-messages/:id',
        props: true,
        component: () => import('@/components/views/compose/ComposeSuccessMessages.vue'),
        meta: {
          ...successRoutesMeta,
          tabTitle: 'pageTabTitle.user.compose.sentSmsBulkDetailsSummary',
          titler: {
            service: 'RouteTitlerService',
          },
        },
      },
      {
        name: composeSuccessScheduledRouteName,
        path: 'success-scheduled/:id',
        props: true,
        component: () => import('@/components/views/compose/ComposeSuccessSendScheduled.vue'),
        meta: {
          ...successRoutesMeta,
          tabTitle: 'pageTabTitle.user.compose.scheduledDetails',
          titler: {
            service: 'RouteTitlerService',
          },
        },
      },
      composeEmailRoute as RouteRecordRaw,
    ],
  },
]
